<script setup lang="ts">
import { ref, watch } from 'vue';
import { type WithAttributes, type Labeled, type Color, type Align, toHtml, fw } from './attributes'

// #region props
const {
  role = 'status',
  ...props
} = defineProps<WithAttributes<{
  class: {
    align?: Align
    color?: Extract<Color, `${string}-solid`>
  }
  role?: 'log' | 'status' | 'alert' | 'progressbar' | 'marquee' | 'timer'
  scrollIntoView?: boolean
}> & Labeled>()
// #endregion props

const alertRef = ref<HTMLElement | null>(null)

watch(alertRef, (el) => {
  if (el && props.scrollIntoView) {
    el.scrollIntoView({ behavior: 'smooth' })
  }
})
</script>

<template>
  <div
    v-bind="toHtml({ role, ...props })"
    ref="alertRef"
    :class="[
      fw.layout.stack,
      fw.padding.large,
      props.class,
      !props.class?.includes('color') && fw.color.blueSolid,
      $style.alert
    ]"
  >
    <slot />
    <footer
      v-if="$slots.actions"
      ui-gap="8"
      :class="[fw.layout.stack, $style.actions]"
    >
      <slot name="actions" />
    </footer>
  </div>
</template>

<style module>
.alert {
  h2, h3, h4 {
    margin-block: 0;
  }

  > .actions {
    margin-left: auto;
  }

  /* Add styles for when alert is used as a notification
  TODO: Refactor according to style guide */
  &:global(.is-notification) {
    position: fixed;
    bottom: 80px;
    right: 15px;
    z-index: 1000;
    min-width: 200px;
    max-width: 400px;

    &:global(.fade-enter-active),
    &:global(.fade-leave-active) {
      transition: all 0.3s ease;
    }

    &:global(.fade-enter-from),
    &:global(.fade-leave-to) {
      opacity: 0;
      transform: translateY(1rem);
    }
  }
}
</style>
